import React, { useCallback } from 'react';
import { AttachmentViewModel } from '@common/model/resource/attachment/viewModel';
import { AttachmentType } from '@common/model/resource/attachment/type';
import { hasIntervalsForToday } from '@common/utils/catalogItem/schedule/hasIntervalsForToday';
import { ResumeAuthorAge, ResumeAuthorName, ResumeCardTitle } from '@cp/entities/resume';
import { useTheme } from '@cp/ds/src/hooks/useTheme';
import { Stack } from '@cp/ds/src/components/stack';
import { useIsMobile } from '@cp/ds/src/hooks/useIsMobile';
import { Typography } from '@cp/ds/src/components/typography';
import { RoutedLink as Link } from 'src/shared/ui/link';
import { ResumeCard as ResumeCardEntity, ResumeCardStats, formatResponseDelay } from 'src/entities/resume';
import { CatalogItemPrice, ICatalogItem } from 'src/entities/catalog';
import { AUDIO_PLAYER_MODAL_NAME } from 'src/app/shared/components/modals/audioPlayer/const';
import { useModal } from 'src/shared/modalProvider';
import { useClientProfile } from 'src/entities/client';
import { isNewSitterResume } from 'src/entities/resume/model';
import { useAuth } from 'src/features/auth';

interface FooterItem {
  label: string;
  value: string;
}

// Disable lazy load for first items
const EAGER_LOAD_FOR_IDX = 3;

export const ResumeCardWidget = ({ data, url, index = 0, ...props }: ICatalogItem) => {
  const permalink = url ? String(url) : undefined;

  const theme = useTheme();
  const isMobile = useIsMobile();
  const { openModal } = useModal();
  const handleMediaButtonClick = useCallback(
    (attachment: AttachmentViewModel) => {
      if (attachment.type === AttachmentType.Audio) {
        openModal(AUDIO_PLAYER_MODAL_NAME, { title: 'Аудио-обращение', src: attachment.src, careerId: data.careerId });
      }
    },
    [data.careerId, openModal],
  );
  const clientProfile = useClientProfile();
  const isAvailableToday = React.useMemo(() => hasIntervalsForToday(data.schedule), [data.schedule]);

  const handleTitleLinkClick: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
    if (!clientProfile) {
      e.stopPropagation();
    }
  };

  const footerItems = React.useMemo<FooterItem[]>(() => {
    const items: FooterItem[] = [];

    if (typeof data.experience === 'number') {
      items.push({
        label: 'лет опыт',
        value: String(data.experience),
      });
    }

    if (data.reviewsCount) {
      items.push({
        label: 'отзывов',
        value: String(data.reviewsCount),
      });
    }

    if (items.length < 2) {
      const delay = formatResponseDelay(data.responseDelay);
      items.push({
        value: delay[0],
        label: delay[1] + ' ответ',
      });
    }

    return items;
  }, [data.experience, data.responseDelay, data.reviewsCount]);

  const isNewSitter = isNewSitterResume(data);

  return (
    <ResumeCardEntity
      data={data}
      footer={
        isMobile ? (
          <Stack direction="column" gap={0.5} sx={{ alignItems: 'left', justifyContent: 'center', p: 1 }}>
            <Link external={Boolean(permalink)} href={permalink} onClick={handleTitleLinkClick} underline={false}>
              <ResumeCardTitle>
                <ResumeAuthorName author={data.clientProfile || {}} />
                <ResumeAuthorAge age={data.clientProfile?.age} />
              </ResumeCardTitle>
            </Link>
            <CatalogItemPrice data={data} sx={{ px: 0, py: 0, backgroundColor: theme.palette.white, color: theme.palette['brand gray'] }} />
            {isNewSitter ? (
              <Typography component="div" variant="small text">
                🎉 Новый ситтер
              </Typography>
            ) : (
              /* isAvailableToday ? */ <Stack alignItems="center" direction="row">
                {footerItems.map(({ value, label }, footerItemIndex) => {
                  const isNotLastFooterItem = footerItemIndex < footerItems.length - 1;
                  return (
                    <React.Fragment key={label}>
                      <Typography variant="small text bold">{value}</Typography>
                      <Typography sx={{ color: 'gray' }} variant="small text">
                        &nbsp;{label}
                      </Typography>
                      {isNotLastFooterItem && (
                        <Typography sx={{ color: 'gray', px: 0.25 }} variant="body bold">
                          •
                        </Typography>
                      )}
                    </React.Fragment>
                  );
                })}
              </Stack>
              // ) : (
              //   <Stack alignItems="center" direction="row" sx={{ color: theme.palette['brand pink hover'], gap: 0.5 }}>
              //     <NotAvailableIcon fontSize="1rem" />
              //     <Typography variant="small text">Сегодня уже занята</Typography>
              //   </Stack>
            )}
          </Stack>
        ) : (
          /* isAvailableToday ? */ <ResumeCardStats careerId={data.careerId} data={data} />
          // ) : (
          //   <Stack
          //     alignItems="center"
          //     direction="row"
          //     justifyContent="center"
          //     sx={{ flexGrow: 1, color: theme.palette['brand pink hover'], gap: 0.5, py: 2 }}
          //   >
          //     <NotAvailableIcon fontSize="1.25rem" />
          //     <Typography sx={{ textTransform: 'uppercase' }} variant="caption bold">
          //       Сегодня уже занята
          //     </Typography>
          //   </Stack>
        )
      }
      header={
        !isMobile && (
          <>
            <Link external={Boolean(permalink)} href={permalink} onClick={handleTitleLinkClick} underline={false}>
              <ResumeCardTitle>
                <ResumeAuthorName author={data.clientProfile || {}} />
                <ResumeAuthorAge age={data.clientProfile?.age} />
              </ResumeCardTitle>
            </Link>
            <CatalogItemPrice data={data} />
          </>
        )
      }
      loadPolicy={index <= EAGER_LOAD_FOR_IDX ? 'eager' : 'lazy'}
      onMediaButtonClick={handleMediaButtonClick}
      showDescription
      sx={{ backgroundColor: theme.palette.background.default }}
      {...props}
    />
  );
};
